import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../Components/Data/AuthContext";

const PRESETS = [25, 50, 100, 250];

export default function DonatePage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [cause, setCause] = useState("General Donation");
  const [causes, setCauses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/program/getAllprograms");
        if (cancelled) return;
        const list = res.data?.programs || res.data?.data || [];
        setCauses(list.map((p) => p.name).slice(0, 12));
      } catch {
        // A failure here only costs the shortcut list, not the ability to give.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveAmount = custom.trim() !== "" ? Number(custom) : amount;
  const amountValid = Number.isFinite(effectiveAmount) && effectiveAmount >= 1;

  async function handleDonate(e) {
    e.preventDefault();
    setError("");

    if (!amountValid) {
      setError("Please enter a donation amount of at least $1.");
      return;
    }

    if (!isAuthenticated) {
      // Checkout is an authenticated endpoint, so send them to sign in and
      // bring them back here afterwards rather than failing with a 401.
      navigate("/signin", { state: { from: "/donate" } });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/users/checkout", {
        items: [
          { name: `EduSpecial donation — ${cause}`, price: effectiveAmount, quantity: 1 },
        ],
        programOrEventName: cause,
      });

      if (res.data?.url) {
        // Full navigation, not a router push: this leaves the SPA for Stripe.
        window.location.href = res.data.url;
      } else {
        setError("Stripe did not return a checkout link. Please try again.");
        setSubmitting(false);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "We couldn't start the checkout. Please try again."
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="es-page">
      <p className="es-eyebrow">Support our work</p>
      <h1 className="es-h1">Make a donation</h1>
      <p className="es-lede">
        Donations fund bursary places for families who could not otherwise
        access specialist therapy. Every contribution goes directly to
        programme delivery.
      </p>

      <div
        style={{
          display: "grid",
          gap: "2.5rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(19rem, 1fr))",
          alignItems: "start",
        }}
      >
        <section className="es-card" aria-labelledby="give-heading">
          <div className="es-card__body">
            <h2 className="es-h2" id="give-heading">Choose an amount</h2>

            {error && (
              <div className="es-alert es-alert--error" role="alert">{error}</div>
            )}

            {!isAuthenticated && (
              <div className="es-alert es-alert--info">
                You'll be asked to sign in before payment, so we can send your
                receipt.
              </div>
            )}

            <form onSubmit={handleDonate}>
              <fieldset style={{ border: 0, padding: 0, margin: "0 0 1rem" }}>
                <legend className="es-label">Preset amounts</legend>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {PRESETS.map((value) => {
                    const active = custom.trim() === "" && amount === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        className={`es-btn ${active ? "es-btn--primary" : "es-btn--ghost"}`}
                        aria-pressed={active}
                        onClick={() => {
                          setAmount(value);
                          setCustom("");
                        }}
                      >
                        ${value}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="es-field">
                <label className="es-label" htmlFor="donate-custom">
                  Or enter your own amount (USD)
                </label>
                <input
                  id="donate-custom"
                  className="es-input"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  placeholder="e.g. 75"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  aria-invalid={custom.trim() !== "" && !amountValid ? "true" : undefined}
                />
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="donate-cause">
                  Direct my gift to
                </label>
                <select
                  id="donate-cause"
                  className="es-select"
                  value={cause}
                  onChange={(e) => setCause(e.target.value)}
                >
                  <option value="General Donation">Where it's needed most</option>
                  <option value="Bursary Fund">Bursary Fund</option>
                  {causes.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="es-btn es-btn--primary"
                disabled={submitting || !amountValid}
                data-testid="donate-submit"
              >
                {submitting
                  ? "Opening secure checkout…"
                  : `Donate $${amountValid ? effectiveAmount : "—"}`}
              </button>

              <p className="es-hint">
                Payments are processed by Stripe. We never see or store your
                card details.
              </p>
            </form>
          </div>
        </section>

        <section aria-labelledby="impact-heading">
          <h2 className="es-h2" id="impact-heading">Where your gift goes</h2>
          <div className="es-card">
            <div className="es-card__body">
              <table className="es-table es-table--numeric">
                <caption className="es-visually-hidden">
                  What each donation amount funds
                </caption>
                <tbody>
                  <tr><td><strong>$25</strong></td><td>One sensory-circuit session</td></tr>
                  <tr><td><strong>$50</strong></td><td>A speech therapy assessment hour</td></tr>
                  <tr><td><strong>$100</strong></td><td>A week of small-group provision</td></tr>
                  <tr><td><strong>$250</strong></td><td>A month of one-to-one support</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
